import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import { ALTURA_TACTIL } from 'src/components/ui/casa';
import { ALTO_TOP_NAV, ANCHO_PANEL, DISPOSICION, ESTADO, geometriaHoja } from 'src/utils/sorbyIa/chatAcoplado';
import ChatSorbyIa from './ChatSorbyIa';
import { useSorbyIa } from './SorbyIaContext';

const boton = { width: ALTURA_TACTIL, height: ALTURA_TACTIL };

// Alto visible real: en el celular el teclado achica visualViewport pero no la ventana.
const useGeometriaHoja = (estado, activo) => {
  const [medida, setMedida] = useState(null);
  useEffect(() => {
    if (!activo) return undefined;
    const vv = window.visualViewport;
    const leer = () =>
      setMedida({
        alturaVentana: window.innerHeight,
        visual: vv ? { height: vv.height, offsetTop: vv.offsetTop } : undefined,
      });
    leer();
    window.addEventListener('resize', leer);
    vv?.addEventListener('resize', leer);
    vv?.addEventListener('scroll', leer);
    return () => {
      window.removeEventListener('resize', leer);
      vv?.removeEventListener('resize', leer);
      vv?.removeEventListener('scroll', leer);
    };
  }, [activo]);
  return medida ? geometriaHoja({ estado, ...medida }) : null;
};

export default function ChatAcopladoSorbyIa() {
  const { visible, disposicion, estado, mover } = useSorbyIa();
  const esPanel = disposicion === DISPOSICION.PANEL;
  const plegado = estado === ESTADO.PLEGADO;
  const ampliado = estado === ESTADO.AMPLIADO;
  const geometria = useGeometriaHoja(estado, visible && !esPanel);

  // El foco acompaña al gesto: al plegar va al control de abrir y al abrir, al de plegar.
  const abrirRef = useRef(null);
  const plegarRef = useRef(null);
  const primeraVez = useRef(true);
  useEffect(() => {
    if (primeraVez.current) {
      primeraVez.current = false;
      return;
    }
    (plegado ? abrirRef : plegarRef).current?.focus();
  }, [plegado]);

  if (!visible) return null;

  const posicion = esPanel
    ? { top: ALTO_TOP_NAV, right: 0, bottom: 0, width: ANCHO_PANEL, borderLeft: 1 }
    : {
        left: 0,
        right: 0,
        bottom: geometria?.abajo ?? 0,
        height: geometria?.alto ?? (plegado ? 56 : '50dvh'),
        borderTop: 1,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        paddingBottom: plegado || geometria?.tecladoAbierto ? 0 : 'env(safe-area-inset-bottom)',
      };

  const botonEscritorio = esPanel && plegado;

  return (
    <>
      {botonEscritorio ? (
        <IconButton
          ref={abrirRef}
          aria-label="Abrir Sorby IA"
          onClick={() => mover('abrir')}
          sx={{
            ...boton,
            position: 'fixed',
            right: 8,
            bottom: 8,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <AutoAwesomeRoundedIcon />
        </IconButton>
      ) : null}
      <Box
        component="aside"
        role="complementary"
        aria-label="Sorby IA"
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.appBar + 1,
          display: botonEscritorio ? 'none' : 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderColor: 'divider',
          ...posicion,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, pr: 0.5, minHeight: ALTURA_TACTIL, flexShrink: 0 }}>
          <AutoAwesomeRoundedIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" component="h2" sx={{ flex: 1 }}>
            Sorby IA
          </Typography>
          {!esPanel && plegado ? (
            <IconButton ref={abrirRef} aria-label="Abrir Sorby IA" onClick={() => mover('abrir')} sx={boton}>
              <KeyboardArrowUpRoundedIcon />
            </IconButton>
          ) : null}
          {!esPanel && !plegado ? (
            <IconButton
              aria-label={ampliado ? 'Bajar Sorby IA a media pantalla' : 'Subir Sorby IA a pantalla completa'}
              onClick={() => mover(ampliado ? 'reducir' : 'ampliar')}
              sx={boton}
            >
              {ampliado ? <CloseFullscreenRoundedIcon /> : <OpenInFullRoundedIcon />}
            </IconButton>
          ) : null}
          {!plegado ? (
            <IconButton ref={plegarRef} aria-label="Plegar Sorby IA" onClick={() => mover('plegar')} sx={boton}>
              {esPanel ? <MenuOpenRoundedIcon sx={{ transform: 'scaleX(-1)' }} /> : <KeyboardArrowDownRoundedIcon />}
            </IconButton>
          ) : null}
        </Box>
        {/* Plegado se oculta pero sigue montado: la conversación y lo escrito no se pierden. */}
        <Box sx={{ flex: 1, minHeight: 0, display: plegado ? 'none' : 'flex', flexDirection: 'column' }}>
          <ChatSorbyIa compacto />
        </Box>
      </Box>
    </>
  );
}
